/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { breakpoints } from '@coursera/cds-core';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import { ChatEmptyState } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import CourseraAIFooter from 'bundles/unified-common/components/coach-on-discovery/CourseraAIFooter';
import CourseraAIHeader from 'bundles/unified-common/components/coach-on-discovery/CourseraAIHeader';
import CourseraAIMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAIMessage';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/front-page';

type Props = {
  headerRef: React.RefObject<HTMLSpanElement>;
  footerRef: React.RefObject<HTMLDivElement>;
};

const styles = {
  chatStyles: css`
    position: fixed;
    bottom: 0;
    top: 105px;
    right: 0;
    min-width: 300px;
    width: 100%;
    max-width: 432px;
    z-index: 1001;
    background: var(--cds-color-neutral-background-primary-weak);

    ${breakpoints.down('sm')} {
      left: 0;
      max-width: 100%;
    }
  `,
  childContainer: css`
    margin: var(--cds-spacing-200);
    position: relative;
    height: calc(100% - var(--cds-spacing-400));

    ${breakpoints.down('sm')} {
      max-width: 100%;
      margin: 0;
      height: 100%;
    }
  `,
};

const CoachChatContainer = ({ headerRef, footerRef }: React.PropsWithChildren<Props>) => {
  const { coachOpen, setCoachOpen, contextConfig } = useCoachState();
  const chatRef = useRef<HTMLDivElement | null>(null);

  const displayTitle = _t('Hi, learner!');
  const displaySubtitle = _t('How can I help?');

  // TODO: adjust chat drawer top and bottom positions to fill the vertical space between page header and the footer
  useEffect(() => {
    const headerHeight = headerRef.current?.getBoundingClientRect()?.height || 105;
    if (chatRef.current) {
      chatRef.current.style.top = `${headerHeight}px`;
      const footerTop = footerRef.current?.getBoundingClientRect()?.top || 0;
      chatRef.current.style.bottom = `${window.innerHeight - footerTop}px`;
    }
  }, [footerRef, headerRef]);

  return (
    <React.Fragment>
      {coachOpen && (
        <div ref={chatRef} css={styles.chatStyles}>
          <div css={styles.childContainer}>
            <CoachChat
              additionalStyles={css`
                padding: 0;
              `}
              showSettings={false}
              mode={contextConfig.config.coachModeId}
              contextConfig={contextConfig}
              onClose={() => setCoachOpen(false)}
              storeSessionId={false}
              emptyState={<ChatEmptyState title={displayTitle} subtitle={displaySubtitle} />}
              customHeader={<CourseraAIHeader onClose={() => setCoachOpen(false)} />}
              renderCustomMessage={(props) => <CourseraAIMessage {...props} />}
              customFooter={<CourseraAIFooter />}
            />
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default CoachChatContainer;
